import { useState, useEffect, useCallback } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { clsx } from "clsx";

import { RVIcon } from "@regal-voice/ui";

import styles from "./ExpandableField.module.scss";

type ExpandableFieldProps = {
    children: any;
    className?: string;
    icon?: JSX.Element;
    isExpanded?: boolean;
    collapsedText: string;
    expandedText: string;
    disabled?: boolean;
    collapsedClassName?: string;
    expandedClassName?: string;
    onClose?: () => void;
    [x: string]: any;
};

export default function ExpandableField({
    children = undefined,
    className = "",
    icon = <RVIcon icon="add-circle-outline" color="primary" />,
    isExpanded = false,
    collapsedText = "",
    expandedText = "",
    collapsedClassName,
    expandedClassName,
    onClose,
    disabled,
    ...props
}: ExpandableFieldProps): JSX.Element {
    const [expanded, setExpanded] = useState(Boolean(isExpanded));

    useEffect(() => {
        if (isExpanded !== expanded) {
            setExpanded(isExpanded);
        }
    }, [isExpanded]);

    const expand = useCallback(
        (newExpanded: boolean) => {
            if (disabled) {
                return;
            }

            setExpanded(newExpanded);
            if (!newExpanded && onClose) {
                onClose();
            }
        },
        [onClose, disabled]
    );

    return (
        <div
            role="expandable-field"
            className={clsx("expandable-form-field-item", className, {
                expanded: expanded,
            })}
            {...props}
        >
            {!expanded && (
                <div
                    role="expand"
                    onClick={() => expand(true)}
                    className={clsx(styles.toggleBasedOnVisibility, {
                        [styles.disabledToggle]: disabled,
                    })}
                >
                    {icon}
                    <span className={collapsedClassName}>{collapsedText}</span>
                </div>
            )}

            <div className={clsx({ ["hidden"]: !expanded })}>
                {children}
                <div
                    role="collapse"
                    onClick={() => expand(false)}
                    className={clsx(styles.toggleBasedOnVisibility, styles.expanded, {
                        [styles.disabledToggle]: disabled,
                    })}
                >
                    <CloseOutlined className={styles.removeSection} />
                    <span className={expandedClassName}>{expandedText}</span>
                </div>
            </div>
        </div>
    );
}
